import React, {useState, useEffect, useCallback} from 'react'
import {createPortal} from 'react-dom'
import NotificationToast from './NotificationToast'
import {NotificationType} from '../../../types/notificationTypes'
import './NotificationToast.css'

interface ToastItem {
  id: string
  type: NotificationType
  claimNumber: string
}

interface NotificationToastContainerProps {
  toasts: ToastItem[]
  onRemoveToast: (id: string) => void
}

// Maximum number of visible toasts at once
const MAX_VISIBLE_TOASTS = 3

/**
 * Container component for managing multiple toast notifications
 * Renders toasts in a fixed position container using portal
 * Limits to MAX_VISIBLE_TOASTS, newer toasts push out older ones
 */
const NotificationToastContainer: React.FC<NotificationToastContainerProps> = ({
  toasts,
  onRemoveToast,
}) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    // Create container element for portal
    let container = document.getElementById('notification-toast-portal')
    if (!container) {
      container = document.createElement('div')
      container.id = 'notification-toast-portal'
      container.className = 'notification-toast-container'
      document.body.appendChild(container)
    }
    setPortalContainer(container)

    return () => {
      // Cleanup on unmount
      if (container && container.childNodes.length === 0) {
        document.body.removeChild(container)
      }
    }
  }, [])

  if (!portalContainer) return null

  // Only show the most recent MAX_VISIBLE_TOASTS
  const visibleToasts = toasts.slice(-MAX_VISIBLE_TOASTS)

  return createPortal(
    <>
      {visibleToasts.map((toast) => (
        <NotificationToast
          key={toast.id}
          type={toast.type}
          claimNumber={toast.claimNumber}
          onClose={() => onRemoveToast(toast.id)}
        />
      ))}
    </>,
    portalContainer
  )
}

/**
 * Custom hook for managing toast notifications
 * Handles adding, removing, and auto-closing toasts
 * Automatically removes oldest toasts when exceeding MAX_VISIBLE_TOASTS
 */
export const useNotificationToast = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const addToast = useCallback((type: NotificationType, claimNumber: string) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newToast: ToastItem = {id, type, claimNumber}

    setToasts((prev) => {
      const updated = [...prev, newToast]
      // Keep only the most recent toasts (double the visible to allow for smooth transitions)
      if (updated.length > MAX_VISIBLE_TOASTS * 2) {
        return updated.slice(-MAX_VISIBLE_TOASTS * 2)
      }
      return updated
    })

    // Auto-close after 8 seconds
    setTimeout(() => {
      removeToast(id)
    }, 8000)

    return id
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const clearAllToasts = useCallback(() => {
    setToasts([])
  }, [])

  return {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
  }
}

export default NotificationToastContainer
export type {ToastItem}
